import React, { useState, useRef, useEffect } from 'react';
import { message } from 'antd';
import dayjs from 'dayjs';
import webStorage from '@/utils/storageManager';
import { useSelector, useDispatch } from 'umi';
import { Select, MenuItem, TextField, Button, Box } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloseIcon from '@material-ui/icons/Close';
import FileDownloadIcon from '@material-ui/icons/GetApp';
import DataToolsStyled from './IndexStyled.js';

const getDictValue = (data, type) => {
  if (!data) {
    return '';
  }
  const d = data.filter((item) => item.dictCode === type)[0] || {};
  return d.dictValue;
};

const VISIT_OPTIONAL_FIELDS = [
  { value: 'utmParam', label: '页面参数', placeholder: '如 source=wx' },
  {
    value: 'clickName',
    label: '按钮名称',
    placeholder: '埋点标识，如 立即跳转',
  },
];

const FilterSection = ({
  filters,
  setFilters,
  onQuery,
  onReset,
  // isQuerying,
}) => {
  const TRACK_APP_ID = webStorage.get('DICTOBJ')?.TRACK_APP_ID;
  const APP_OPTIONS = [
    { value: getDictValue(TRACK_APP_ID, 'SMG Stage Meet'), label: '台上见' },
    { value: getDictValue(TRACK_APP_ID, 'LOL ARCANE'), label: '双城之战' },
  ];

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleEventTypeChange = (type) => {
    setFilters((prev) => ({
      ...prev,
      eventType: type,
      templateId: '',
      pagePath: '',
      conditions: [],
    }));
  };

  const addCondition = (field) => {
    setFilters((prev) => ({
      ...prev,
      conditions: [
        ...(prev.conditions || []),
        { id: Math.random().toString(36).substr(2, 9), field, value: '' },
      ],
    }));
    setIsDropdownOpen(false);
  };

  const removeCondition = (id) => {
    setFilters((prev) => ({
      ...prev,
      conditions: (prev.conditions || []).filter((c) => c.id !== id),
    }));
  };

  const updateConditionValue = (id, value) => {
    setFilters((prev) => ({
      ...prev,
      conditions: (prev.conditions || []).map((c) =>
        c.id === id ? { ...c, value } : c,
      ),
    }));
  };

  const conditions = filters.conditions || [];
  const availableFields = VISIT_OPTIONAL_FIELDS.filter(
    (opt) => !conditions.some((cond) => cond.field === opt.value),
  );

  return (
    <div className="filter-section">
      <div className="filter-header">
        <h2>筛选条件</h2>
      </div>

      <div className="filter-content">
        <div className="filter-group">
          <div className="filter-item">
            <div className="filter-label">
              小程序 <span className="required">*</span>
            </div>
            <div className="filter-row">
              <Select
                name="miniProgram"
                value={filters.miniProgram}
                onChange={handleInputChange}
                variant="standard"
                className="filter-select"
              >
                {APP_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>

          <div className="filter-item">
            <div className="filter-label">
              时间范围 <span className="required">*</span>
            </div>
            <div className="filter-row">
              <TextField
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleInputChange}
                variant="standard"
                size="small"
                className="filter-input"
              />
              <span className="date-separator">至</span>
              <TextField
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleInputChange}
                variant="standard"
                size="small"
                className="filter-input"
              />
            </div>
          </div>

          <div className="filter-item">
            <div className="filter-label">
              事件类型 <span className="required">*</span>
            </div>
            <div className="filter-row">
              <div className="event-type-tabs">
                <button
                  type="button"
                  onClick={() => handleEventTypeChange('visit')}
                  className={`event-type-tab ${
                    filters.eventType === 'visit' ? 'active' : ''
                  }`}
                >
                  访问了页面
                </button>
                <button
                  type="button"
                  onClick={() => handleEventTypeChange('subscribe')}
                  className={`event-type-tab ${
                    filters.eventType === 'subscribe' ? 'active' : ''
                  }`}
                >
                  订阅了订阅消息
                </button>
              </div>
            </div>
          </div>
        </div>

        {filters.eventType && (
          <div className="filter-group">
            <div className="filter-label">
              {filters.eventType === 'subscribe' ? '模板条件' : '页面条件'}{' '}
              <span className="required">*</span>
            </div>

            {filters.eventType === 'subscribe' && (
              <div className="filter-row">
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    px: 2,
                    py: 1,
                    background: '#e6f7ff',
                    border: '1px solid #91d5ff',
                    color: '#1890ff',
                    borderRadius: 1,
                    fontSize: 14,
                    fontWeight: 500,
                    width: 128,
                    textAlign: 'center',
                  }}
                >
                  模板ID
                </Box>
                <TextField
                  type="text"
                  name="templateId"
                  value={filters.templateId}
                  onChange={handleInputChange}
                  placeholder="必填，如 msg_001"
                  variant="standard"
                  size="small"
                  className="filter-input"
                />
              </div>
            )}

            {filters.eventType === 'visit' && (
              <div>
                <div className="filter-row" style={{ marginBottom: 16 }}>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 1,
                      background: '#e6f7ff',
                      border: '1px solid #91d5ff',
                      color: '#1890ff',
                      borderRadius: 1,
                      fontSize: 14,
                      fontWeight: 500,
                      width: 128,
                      textAlign: 'center',
                    }}
                  >
                    页面名称
                  </Box>
                  <TextField
                    type="text"
                    name="pageName"
                    value={filters.pageName}
                    onChange={handleInputChange}
                    placeholder="必填，如 首页"
                    variant="standard"
                    size="small"
                    className="filter-input"
                  />
                </div>
                <div className="filter-row" style={{ marginBottom: 16 }}>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 1,
                      background: '#e6f7ff',
                      border: '1px solid #91d5ff',
                      color: '#1890ff',
                      borderRadius: 1,
                      fontSize: 14,
                      fontWeight: 500,
                      width: 128,
                      textAlign: 'center',
                    }}
                  >
                    页面路径
                  </Box>
                  <TextField
                    type="text"
                    name="pagePath"
                    value={filters.pagePath}
                    onChange={handleInputChange}
                    placeholder="必填，如 pages/tabs/home/index"
                    variant="standard"
                    size="small"
                    className="filter-input"
                  />
                </div>

                {conditions.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    {conditions.map((cond) => {
                      const fieldDef = VISIT_OPTIONAL_FIELDS.find(
                        (f) => f.value === cond.field,
                      );
                      return (
                        <div
                          key={cond.id}
                          className="filter-row"
                          style={{ marginBottom: 12 }}
                        >
                          <Box
                            component="span"
                            sx={{
                              position: 'relative',
                              display: 'inline-block',
                              px: 2,
                              py: 1,
                              background: '#f5f5f5',
                              border: '1px solid #d9d9d9',
                              borderRadius: 1,
                              fontSize: 14,
                              fontWeight: 500,
                              width: 128,
                              textAlign: 'center',
                            }}
                          >
                            {fieldDef?.label}
                            <span
                              style={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                fontSize: 10,
                                background: '#e0e0e0',
                                color: '#666',
                                padding: '2px 6px',
                                borderRadius: 10,
                              }}
                            >
                              选填
                            </span>
                          </Box>
                          <TextField
                            type="text"
                            value={cond.value}
                            onChange={(e) =>
                              updateConditionValue(cond.id, e.target.value)
                            }
                            placeholder={fieldDef?.placeholder}
                            variant="standard"
                            size="small"
                            className="filter-input"
                          />
                          <Button
                            onClick={() => removeCondition(cond.id)}
                            size="small"
                            style={{ color: '#999' }}
                          >
                            <CloseIcon fontSize="small" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {availableFields.length > 0 && (
                  <div
                    style={{ position: 'relative', display: 'inline-block' }}
                  >
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="add-field-btn"
                    >
                      添加选填字段
                      <ExpandMoreIcon
                        style={{
                          transform: isDropdownOpen
                            ? 'rotate(180deg)'
                            : 'rotate(0)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </button>

                    {isDropdownOpen && (
                      <div className="dropdown-menu">
                        {availableFields.map((opt) => (
                          <button
                            type="button"
                            key={opt.value}
                            onClick={() => addCondition(opt.value)}
                            className="dropdown-item"
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="filter-footer">
        <Button onClick={onReset}>
          <RotateRightIcon />
          重置
        </Button>
        <Button onClick={onQuery} color="primary">
          <SearchIcon />
          查询预览
        </Button>
      </div>
    </div>
  );
};

const PreviewSection = ({
  totalDataCount = 0,
  totalMemberCount = 0,
  onExport,
  onCancel,
}) => {
  return (
    <div className="preview-section">
      <div className="preview-header">
        <div className="preview-title">
          <h2>预览与导出</h2>
        </div>
      </div>

      <div className="preview-content">
        <div className="data-summary">
          <span className="label">匹配数据总量：</span>
          <span className="value success">{totalDataCount}</span>
          <span className="unit">条，匹配人数：</span>
          <span className="value success">{totalMemberCount}</span>
          <span className="unit">人</span>
        </div>

        {totalDataCount === 0 ? (
          <div className="empty-state">
            <p>暂无匹配数据，请调整筛选条件</p>
          </div>
        ) : null}
      </div>

      <div className="preview-footer">
        <Button onClick={onCancel}>
          <CloseIcon fontSize="small" />
          取消
        </Button>
        <Button onClick={onExport} color="primary">
          <FileDownloadIcon fontSize="small" />
          导出 CSV
        </Button>
      </div>
    </div>
  );
};

const DataTools = () => {
  const dispatch = useDispatch();
  const { trackingData } = useSelector((state) => state.dataTools) || {};

  const TRACK_APP_ID = webStorage.get('DICTOBJ')?.TRACK_APP_ID;
  const defaultFilters = {
    startDate: dayjs().format('YYYY-MM-DD'),
    // endDate: dayjs().substr(6).format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    eventType: 'visit', // visit:访问页面, subscribe:订阅了订阅消息
    templateId: '',
    pageName: '', // 首页
    pagePath: '', // pages/tabs/home/index
    miniProgram: getDictValue(TRACK_APP_ID, 'SMG Stage Meet'),
    conditions: [],
  };

  const [filters, setFilters] = useState(defaultFilters);
  const [isQuerying, setIsQuerying] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  // const [previewData, setPreviewData] = useState([]);

  // 表单校验
  const validateForm = () => {
    const startD = dayjs(filters.startDate);
    const endD = dayjs(filters.endDate);

    if (!startD || !endD) {
      message.error('请输入有效的日期');
      setIsQuerying(false);
      return false;
    }

    const daysDiff = (endD - startD) / (1000 * 60 * 60 * 24);
    if (daysDiff > 90) {
      message.error('时间范围不能超过90天');
      setIsQuerying(false);
      return false;
    }
    if (daysDiff < 0) {
      message.error('开始时间不能晚于结束时间');
      setIsQuerying(false);
      return false;
    }

    if (!filters.eventType) {
      message.error('请选择事件类型');
      return false;
    }
    if (filters.eventType === 'subscribe' && !filters.templateId.trim()) {
      message.error('请填写模板ID');
      return false;
    }
    if (filters.eventType === 'visit' && !filters.pageName.trim()) {
      message.error('请填写页面名称');
      return false;
    }
    if (filters.eventType === 'visit' && !filters.pagePath.trim()) {
      message.error('请填写页面路径');
      return false;
    }
    return true;
  };

  const handleQuery = async () => {
    if (isQuerying) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    const values = {
      appId: filters.miniProgram,
      path: filters.pagePath,
      pathName: filters.pageName,
      templateId: filters.templateId,
      startDate: filters.startDate,
      endDate: filters.endDate,
      utmParam:
        filters.conditions?.find((c) => c.field === 'utmParam')?.value || '',
      clickName:
        filters.conditions?.find((c) => c.field === 'clickName')?.value || '',
    };

    Object.keys(values).forEach((key) => {
      if (!values[key]) {
        delete values[key];
      }
    });

    setIsQuerying(true);
    dispatch({
      type:
        filters.eventType === 'visit'
          ? 'dataTools/getTrackingMemberList'
          : 'dataTools/getTemplateMemberList',
      payload: {
        values,
      },
      callback: () => {
        setHasQueried(true);
        setIsQuerying(false);
      },
    });
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    setHasQueried(false);
    // setTotalDataCount(0);
    // setTotalMemberCount(0);
    // setPreviewData([]);
    dispatch({
      type: 'dataTools/save',
      payload: {
        trackingData: {},
      },
    });
  };

  const handleExport = () => {
    if (trackingData?.pv > 100000) {
      message.error('超过10万条，暂无法导出！');
      return;
    }

    if (!validateForm()) {
      return;
    }

    const values = {
      appId: filters.miniProgram,
      path: filters.pagePath,
      pathName: filters.pathName,
      templateId: filters.templateId,
      startDate: filters.startDate,
      endDate: filters.endDate,
      utmParam:
        filters.conditions?.find((c) => c.field === 'utmParam')?.value || '',
      clickName:
        filters.conditions?.find((c) => c.field === 'clickName')?.value || '',
    };

    Object.keys(values).forEach((key) => {
      if (!values[key]) {
        delete values[key];
      }
    });

    dispatch({
      type:
        filters.eventType === 'visit'
          ? 'dataTools/exportTrackingMember'
          : 'dataTools/exportTemplateMember',
      payload: {
        values,
      },
      callback: () => {
        message.success('数据下载中，可在我的任务查看导出结果');
      },
    });
  };

  return (
    <DataToolsStyled>
      {/* <div className="page-header">
        <h1>活动数据取数工具</h1>
        <p>快速查询并导出用户活动数据。</p>
      </div> */}

      <FilterSection
        filters={filters}
        setFilters={setFilters}
        onQuery={handleQuery}
        onReset={handleReset}
        isQuerying={isQuerying}
      />

      {hasQueried && (
        <PreviewSection
          totalDataCount={trackingData?.pv || 0}
          totalMemberCount={trackingData?.uv || 0}
          // previewData={previewData}
          onExport={handleExport}
          onCancel={() => setHasQueried(false)}
        />
      )}
    </DataToolsStyled>
  );
};

export default DataTools;
